/**
 * AppointmentForm (B1.6) — 3-step create/edit wizard rendered inside a SideSheet.
 *
 * Authority:
 *   - UX Blueprint §B1.6 — "Claude-owned smart wrapper that composes Lovable
 *     field primitives (Input/Select/Textarea/Switch)".
 *   - UX Invariant §9 — 56px+ touch targets, no modals (side sheet only),
 *     color+icon statuses, no hardcoded user-facing strings.
 *
 * Flow:
 *   Step 1 · Customer & Pet
 *     - searchable customer list (name + phone + optional VIP badge)
 *     - pet select (dependent on customer; hint when empty)
 *   Step 2 · Service & Staff
 *     - services grouped by category, duration + price shown
 *     - staff list filtered by service.qualifiedStaffIds; hint when
 *       selected staff is not qualified (soft warning, not blocked)
 *   Step 3 · Date & Time
 *     - date + start/end (start defaults to slot time, end auto-computed
 *       from service.durationMin but editable)
 *     - price + currency (prefilled from service, editable)
 *     - notes (optional), VIP toggle (mirrors customer.isVip by default)
 *     - conflict warning when another appointment overlaps for the staff
 *
 * Submit:
 *   - create mode → `useCreateAppointment(isoMonday).mutateAsync(payload)`
 *   - edit mode   → `useEditAppointment(isoMonday).mutateAsync({ id, changes })`
 *   - toasts: createSuccess / editSuccess / submitFailed
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { CaretLeft, CaretRight, Check, Crown, MagnifyingGlass, Warning } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/Button';
import { FieldError } from '@/shared/ui/FieldError';
import { Input } from '@/shared/ui/Input';
import { Label } from '@/shared/ui/Label';
import { Select } from '@/shared/ui/Select';
import { Switch } from '@/shared/ui/Switch';
import { Textarea } from '@/shared/ui/Textarea';
import { cn } from '@/shared/utils/cn';

import {
  FORM_STEPS,
  STEP_FIELDS,
  appointmentFormSchema,
  type AppointmentFormStep,
  type AppointmentFormValues,
} from './appointment-form-schema';
import {
  addMinutesToTime,
  combineDateTime,
  diffMinutes,
  hasStaffConflict,
} from './form-utils';
import {
  useCreateAppointment,
  useCustomers,
  useEditAppointment,
  usePets,
  useServices,
  useStaffList,
  useWeekAppointments,
} from './useAppointments';
import type {
  AppointmentRecord,
  CreateAppointmentPayload,
} from './types';

const CURRENCIES = ['TRY', 'USD', 'EUR'] as const;

export interface AppointmentFormProps {
  /**
   * Monday ISO for the active calendar week — used as React Query cache key
   * so mutations invalidate the visible week.
   */
  isoMonday: string;
  mode: 'create' | 'edit';
  /** Pre-fill values. Required fields in edit mode; optional in create. */
  initialValues?: Partial<AppointmentFormValues> & { id?: string };
  onSuccess: () => void;
  onCancel: () => void;
}

function defaultValues(
  override?: Partial<AppointmentFormValues>,
): AppointmentFormValues {
  const today = new Date();
  const y = today.getFullYear();
  const m = `${today.getMonth() + 1}`.padStart(2, '0');
  const d = `${today.getDate()}`.padStart(2, '0');
  return {
    customerId: '',
    petId: '',
    serviceId: '',
    staffId: '',
    date: `${y}-${m}-${d}`,
    startTime: '09:00',
    endTime: '10:00',
    price: 0,
    currency: 'TRY',
    notes: '',
    isVip: false,
    ...override,
  };
}

export function AppointmentForm({
  isoMonday,
  mode,
  initialValues,
  onSuccess,
  onCancel,
}: AppointmentFormProps): JSX.Element {
  const { t } = useTranslation('appointmentForm');
  const { t: tCalendar } = useTranslation('calendar');

  const [stepIndex, setStepIndex] = useState(0);
  const [customerFilter, setCustomerFilter] = useState('');

  const form = useForm<AppointmentFormValues>({
    resolver: zodResolver(appointmentFormSchema),
    defaultValues: defaultValues(initialValues),
    mode: 'onTouched',
  });
  const {
    control,
    register,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting },
  } = form;

  const values = watch();

  const customersQuery = useCustomers();
  const petsQuery = usePets(values.customerId || undefined);
  const servicesQuery = useServices();
  const staffQuery = useStaffList();
  const appointmentsQuery = useWeekAppointments(isoMonday);

  const createMutation = useCreateAppointment(isoMonday);
  const editMutation = useEditAppointment(isoMonday);

  const customers = customersQuery.data;
  const pets = petsQuery.data;
  const services = servicesQuery.data;
  const staff = staffQuery.data;
  const existingAppointments = appointmentsQuery.data;

  const filteredCustomers = useMemo(() => {
    const list = customers ?? [];
    const q = customerFilter.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      c => c.fullName.toLowerCase().includes(q) || c.phone.includes(q),
    );
  }, [customers, customerFilter]);

  const selectedCustomer = customers?.find(c => c.id === values.customerId);
  const selectedPet = pets?.find(p => p.id === values.petId);
  const selectedService = services?.find(s => s.id === values.serviceId);
  const selectedStaff = staff?.find(s => s.id === values.staffId);

  const servicesByCategory = useMemo(() => {
    const map = new Map<string, NonNullable<typeof services>>();
    for (const svc of services ?? []) {
      const bucket = map.get(svc.category) ?? [];
      bucket.push(svc);
      map.set(svc.category, bucket);
    }
    return Array.from(map.entries());
  }, [services]);

  const qualifiedStaff = useMemo(() => {
    const list = staff ?? [];
    if (!selectedService) return list.filter(s => s.isActive);
    if (!selectedService.qualifiedStaffIds?.length) {
      return list.filter(s => s.isActive);
    }
    const qset = new Set(selectedService.qualifiedStaffIds);
    return list.filter(s => s.isActive && qset.has(s.id));
  }, [staff, selectedService]);

  const staffQualifiedForService =
    !selectedService ||
    !selectedService.qualifiedStaffIds?.length ||
    (values.staffId && selectedService.qualifiedStaffIds.includes(values.staffId));

  const conflict =
    values.staffId && values.date && values.startTime && values.endTime
      ? hasStaffConflict({
          existing: existingAppointments ?? [],
          staffId: values.staffId,
          date: values.date,
          startTime: values.startTime,
          endTime: values.endTime,
          ignoreId: initialValues?.id,
        })
      : false;

  const handleServiceChange = (serviceId: string) => {
    setValue('serviceId', serviceId, { shouldValidate: true });
    const svc = services?.find(s => s.id === serviceId);
    if (!svc) return;
    setValue('price', svc.price, { shouldValidate: true });
    setValue('currency', svc.currency, { shouldValidate: false });
    if (values.startTime) {
      setValue('endTime', addMinutesToTime(values.startTime, svc.durationMin), {
        shouldValidate: true,
      });
    }
    if (svc.qualifiedStaffIds?.length && !svc.qualifiedStaffIds.includes(values.staffId)) {
      setValue('staffId', svc.qualifiedStaffIds[0], { shouldValidate: true });
    }
  };

  const handleStartTimeChange = (newStart: string) => {
    setValue('startTime', newStart, { shouldValidate: true });
    if (selectedService) {
      setValue(
        'endTime',
        addMinutesToTime(newStart, selectedService.durationMin),
        { shouldValidate: true },
      );
    }
  };

  const handleCustomerChange = (customerId: string) => {
    setValue('customerId', customerId, { shouldValidate: true });
    setValue('petId', '', { shouldValidate: false });
    const cust = customers?.find(c => c.id === customerId);
    if (cust) setValue('isVip', cust.isVip);
  };

  const currentStep: AppointmentFormStep = FORM_STEPS[stepIndex];

  const goNext = async () => {
    const fieldsToValidate = STEP_FIELDS[currentStep];
    const valid = await trigger(fieldsToValidate, { shouldFocus: true });
    if (!valid) return;
    if (stepIndex < FORM_STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    }
  };

  const goBack = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const onSubmit = handleSubmit(async data => {
    if (!selectedCustomer || !selectedPet || !selectedService) return;

    const start = combineDateTime(data.date, data.startTime).toISOString();
    const end = combineDateTime(data.date, data.endTime).toISOString();

    try {
      if (mode === 'create') {
        const payload: CreateAppointmentPayload = {
          customerId: data.customerId,
          customerName: selectedCustomer.fullName,
          petId: data.petId,
          petName: selectedPet.name,
          serviceId: data.serviceId,
          serviceName: selectedService.name,
          serviceCategory: selectedService.category,
          staffId: data.staffId,
          startTime: start,
          endTime: end,
          isVip: data.isVip,
          notes: data.notes?.trim() ? data.notes.trim() : undefined,
          price: data.price,
          currency: data.currency,
        };
        await createMutation.mutateAsync(payload);
        toast.success(t('toasts.createSuccess'));
      } else if (mode === 'edit' && initialValues?.id) {
        const changes: Partial<AppointmentRecord> = {
          customerId: data.customerId,
          customerName: selectedCustomer.fullName,
          petId: data.petId,
          petName: selectedPet.name,
          serviceName: selectedService.name,
          serviceCategory: selectedService.category,
          staffId: data.staffId,
          startTime: start,
          endTime: end,
          isVip: data.isVip,
          notes: data.notes?.trim() ? data.notes.trim() : undefined,
          price: data.price,
          currency: data.currency,
        };
        await editMutation.mutateAsync({ id: initialValues.id, changes });
        toast.success(t('toasts.editSuccess'));
      }
      onSuccess();
    } catch {
      toast.error(t('errors.submitFailed'));
    }
  });

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-biz-content-lg" onSubmit={onSubmit} noValidate>
        <Stepper currentIndex={stepIndex} />

        {currentStep === 'customer' && (
          <section aria-labelledby="step-customer" className="space-y-biz-content-lg">
            <div>
              <Label id="step-customer">{t('customerLabel')}</Label>
              <div className="relative">
                <MagnifyingGlass
                  size={18}
                  weight="regular"
                  aria-hidden="true"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-biz-muted-foreground"
                />
                <Input
                  type="search"
                  className="pl-10"
                  placeholder={t('customerSearchPlaceholder')}
                  value={customerFilter}
                  onChange={e => setCustomerFilter(e.target.value)}
                />
              </div>
              <div
                role="radiogroup"
                aria-label={t('customerLabel')}
                className="mt-biz-content max-h-72 overflow-y-auto biz-scrollbar rounded-biz-card border border-biz-border"
              >
                {filteredCustomers.length === 0 ? (
                  <div className="px-biz-card py-biz-content-lg text-biz-sm text-biz-muted-foreground">
                    {t('petNoneHint')}
                  </div>
                ) : (
                  <ul className="divide-y divide-biz-border">
                    {filteredCustomers.map(c => {
                      const active = c.id === values.customerId;
                      return (
                        <li key={c.id}>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => handleCustomerChange(c.id)}
                            className={cn(
                              'w-full flex items-center justify-between gap-biz-content-lg',
                              'min-h-biz-touch px-biz-card py-biz-content text-left',
                              'hover:bg-biz-muted transition-colors duration-biz-fast',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
                              active && 'bg-biz-primary-subtle',
                            )}
                          >
                            <div className="min-w-0">
                              <div className="text-biz-base font-medium flex items-center gap-biz-content">
                                {c.fullName}
                                {c.isVip && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-biz-accent-subtle px-2 py-0.5 text-xs font-semibold text-biz-accent">
                                    <Crown size={12} weight="regular" /> {t('customerVipBadge')}
                                  </span>
                                )}
                              </div>
                              <div className="text-biz-sm font-mono-num text-biz-muted-foreground">
                                {c.phone}
                              </div>
                            </div>
                            {active && <Check size={20} weight="regular" className="text-biz-primary" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <FieldError>
                {errors.customerId ? t(`errors.${errors.customerId.message}`) : null}
              </FieldError>
            </div>

            <div>
              <Label htmlFor="petId">{t('petLabel')}</Label>
              <Select
                id="petId"
                invalid={Boolean(errors.petId)}
                disabled={!values.customerId}
                {...register('petId')}
              >
                <option value="">{t('petPlaceholder')}</option>
                {(pets ?? []).map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.breed ? ` · ${p.breed}` : ''}
                  </option>
                ))}
              </Select>
              {!values.customerId && (
                <p className="mt-biz-content text-biz-sm text-biz-muted-foreground">
                  {t('petNoneHint')}
                </p>
              )}
              {values.customerId && (pets?.length ?? 0) === 0 && (
                <p className="mt-biz-content text-biz-sm text-biz-muted-foreground">
                  {t('petNoPetsHint')}
                </p>
              )}
              <FieldError>
                {errors.petId ? t(`errors.${errors.petId.message}`) : null}
              </FieldError>
            </div>
          </section>
        )}

        {currentStep === 'service' && (
          <section aria-labelledby="step-service" className="space-y-biz-content-lg">
            <div>
              <Label id="step-service" htmlFor="serviceId">
                {t('serviceLabel')}
              </Label>
              <Select
                id="serviceId"
                invalid={Boolean(errors.serviceId)}
                value={values.serviceId}
                onChange={e => handleServiceChange(e.target.value)}
              >
                <option value="">{t('servicePlaceholder')}</option>
                {servicesByCategory.map(([category, group]) => (
                  <optgroup
                    key={category}
                    label={t(`serviceGroup.${category}`, { defaultValue: category })}
                  >
                    {group.map(svc => (
                      <option key={svc.id} value={svc.id}>
                        {svc.name} · {t('serviceDuration', { min: svc.durationMin })} · {svc.price} {svc.currency}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
              <FieldError>
                {errors.serviceId ? t(`errors.${errors.serviceId.message}`) : null}
              </FieldError>
            </div>

            <div>
              <Label htmlFor="staffId">{t('staffLabel')}</Label>
              <Select
                id="staffId"
                invalid={Boolean(errors.staffId)}
                {...register('staffId')}
              >
                <option value="">{t('staffPlaceholder')}</option>
                {qualifiedStaff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.fullName} · {s.role}
                  </option>
                ))}
              </Select>
              {selectedService?.qualifiedStaffIds?.length &&
                !staffQualifiedForService && (
                  <div
                    role="alert"
                    className="mt-biz-content flex items-start gap-biz-content rounded-biz-card border border-biz-warning-border bg-biz-warning-subtle px-biz-content-lg py-biz-content text-biz-sm text-biz-foreground"
                  >
                    <Warning size={18} weight="regular" className="shrink-0 mt-0.5 text-biz-warning" />
                    <span>{t('staffUnqualifiedHint')}</span>
                  </div>
                )}
              <FieldError>
                {errors.staffId ? t(`errors.${errors.staffId.message}`) : null}
              </FieldError>
            </div>
          </section>
        )}

        {currentStep === 'schedule' && (
          <section aria-labelledby="step-schedule" className="space-y-biz-content-lg">
            <div>
              <Label id="step-schedule" htmlFor="date">
                {t('dateLabel')}
              </Label>
              <Input
                id="date"
                type="date"
                invalid={Boolean(errors.date)}
                {...register('date')}
              />
              <FieldError>
                {errors.date ? t(`errors.${errors.date.message}`) : null}
              </FieldError>
            </div>

            <div className="grid grid-cols-2 gap-biz-content-lg">
              <div>
                <Label htmlFor="startTime">{t('startLabel')}</Label>
                <Input
                  id="startTime"
                  type="time"
                  invalid={Boolean(errors.startTime)}
                  value={values.startTime}
                  onChange={e => handleStartTimeChange(e.target.value)}
                />
                <FieldError>
                  {errors.startTime ? t(`errors.${errors.startTime.message}`) : null}
                </FieldError>
              </div>
              <div>
                <Label htmlFor="endTime">{t('endLabel')}</Label>
                <Input
                  id="endTime"
                  type="time"
                  invalid={Boolean(errors.endTime)}
                  {...register('endTime')}
                />
                <FieldError>
                  {errors.endTime ? t(`errors.${errors.endTime.message}`) : null}
                </FieldError>
              </div>
            </div>
            {selectedService && (
              <p className="text-biz-sm text-biz-muted-foreground -mt-biz-content-lg">
                {t('endComputedHint')}
              </p>
            )}

            {conflict && (
              <div
                role="alert"
                className="flex items-start gap-biz-content rounded-biz-card border border-biz-danger bg-biz-danger/10 px-biz-content-lg py-biz-content text-biz-sm text-biz-foreground"
              >
                <Warning size={18} weight="regular" className="shrink-0 mt-0.5 text-biz-danger" />
                <span>{t('errors.conflict')}</span>
              </div>
            )}

            <div className="grid grid-cols-3 gap-biz-content-lg">
              <div className="col-span-2">
                <Label htmlFor="price">{t('priceLabel')}</Label>
                <Controller
                  control={control}
                  name="price"
                  render={({ field }) => (
                    <Input
                      id="price"
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      className="font-mono-num"
                      invalid={Boolean(errors.price)}
                      value={Number.isFinite(field.value) ? field.value : 0}
                      onChange={e => field.onChange(Number(e.target.value))}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                <FieldError>
                  {errors.price ? t(`errors.${errors.price.message}`) : null}
                </FieldError>
              </div>
              <div>
                <Label htmlFor="currency">{t('currencyLabel')}</Label>
                <Select id="currency" {...register('currency')}>
                  {CURRENCIES.map(cur => (
                    <option key={cur} value={cur}>
                      {cur}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">{t('notesLabel')}</Label>
              <Textarea
                id="notes"
                rows={3}
                placeholder={t('notesPlaceholder')}
                {...register('notes')}
              />
            </div>

            <Controller
              control={control}
              name="isVip"
              render={({ field }) => (
                <label className="flex items-center gap-biz-content-lg cursor-pointer">
                  <Switch
                    checked={Boolean(field.value)}
                    onCheckedChange={checked => field.onChange(checked)}
                    aria-label={t('markVip')}
                  />
                  <span className="text-biz-sm text-biz-foreground">{t('markVip')}</span>
                </label>
              )}
            />

            {selectedCustomer && selectedPet && selectedService && selectedStaff && (
              <aside
                aria-label={t('summary.title')}
                className="rounded-biz-card border border-biz-border bg-biz-surface p-biz-card space-y-biz-content"
              >
                <div className="text-biz-xs font-semibold uppercase tracking-wider text-biz-muted-foreground">
                  {t('summary.title')}
                </div>
                <SummaryRow label={t('summary.customer')} value={selectedCustomer.fullName} />
                <SummaryRow label={t('summary.pet')} value={selectedPet.name} />
                <SummaryRow
                  label={t('summary.service')}
                  value={selectedService.name}
                />
                <SummaryRow label={t('summary.staff')} value={selectedStaff.fullName} />
                <SummaryRow
                  label={t('summary.when')}
                  value={`${values.date} · ${values.startTime} – ${values.endTime}`}
                  mono
                />
                <SummaryRow
                  label={t('summary.duration')}
                  value={t('serviceDuration', {
                    min: Math.max(
                      0,
                      diffMinutes(values.startTime, values.endTime),
                    ),
                  })}
                  mono
                />
                <SummaryRow
                  label={t('summary.price')}
                  value={`${values.price ?? 0} ${values.currency}`}
                  mono
                />
              </aside>
            )}

            {selectedService && (
              <p className="sr-only">
                {tCalendar('statusLabels.confirmed')}
              </p>
            )}
          </section>
        )}

        <div className="flex items-center justify-between gap-biz-content-lg pt-biz-content-lg border-t border-biz-border">
          <div>
            {stepIndex > 0 && (
              <Button
                type="button"
                intent="ghost"
                leftIcon={<CaretLeft size={18} weight="regular" />}
                onClick={goBack}
              >
                {t('actions.back')}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-biz-content-lg">
            <Button type="button" intent="secondary" onClick={onCancel}>
              {t('actions.cancel')}
            </Button>
            {stepIndex < FORM_STEPS.length - 1 ? (
              <Button
                type="button"
                intent="primary"
                rightIcon={<CaretRight size={18} weight="regular" />}
                onClick={goNext}
              >
                {t('actions.next')}
              </Button>
            ) : (
              <Button
                type="submit"
                intent="primary"
                loading={isSubmitting || createMutation.isPending || editMutation.isPending}
                leftIcon={<Check size={18} weight="regular" />}
              >
                {mode === 'create' ? t('actions.create') : t('actions.save')}
              </Button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

function Stepper({ currentIndex }: { currentIndex: number }): JSX.Element {
  const { t } = useTranslation('appointmentForm');
  return (
    <ol className="flex items-center gap-biz-content" aria-label="steps">
      {FORM_STEPS.map((step, index) => {
        const active = index === currentIndex;
        const done = index < currentIndex;
        return (
          <li key={step} className="flex-1 flex items-center gap-biz-content">
            <div
              aria-current={active ? 'step' : undefined}
              className={cn(
                'inline-flex items-center justify-center shrink-0',
                'h-8 w-8 rounded-biz-full text-biz-sm font-semibold',
                done && 'bg-biz-success text-biz-success-foreground',
                active && 'bg-biz-primary text-biz-primary-foreground',
                !done && !active && 'bg-biz-muted text-biz-muted-foreground',
              )}
            >
              {done ? <Check size={16} weight="regular" /> : index + 1}
            </div>
            <span
              className={cn(
                'text-biz-sm',
                active ? 'font-semibold text-biz-foreground' : 'text-biz-muted-foreground',
                'hidden sm:inline',
              )}
            >
              {t(`stepShort.${step}`)}
            </span>
            {index < FORM_STEPS.length - 1 && (
              <span
                aria-hidden="true"
                className={cn(
                  'flex-1 h-px',
                  done ? 'bg-biz-success' : 'bg-biz-border',
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function SummaryRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-biz-content-lg">
      <span className="text-biz-xs font-semibold uppercase tracking-wider text-biz-muted-foreground">
        {label}
      </span>
      <span className={cn('text-biz-sm text-right', mono && 'font-mono-num')}>
        {value}
      </span>
    </div>
  );
}
